import { faArrowDown, faArrowLeft, faArrowRight, faHome } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropCell } from 'components/SpaceBooking/SpaceTable/DropCell';
import { RefetchContextProvider } from 'components/SpaceBooking/SpaceTable/RefetchContext';
import { SpaceTableLoader } from 'components/SpaceBooking/SpaceTable/SpaceTableLoader';
import { MappedSpace } from 'components/SpaceBooking/SpaceTable/types';
import { refTable } from 'components/TrainingPlan/TrainingPlanHelpers';
import { Button } from 'components/styledComponents/Button';
import Flex from 'components/styledComponents/Flex';
import { InjectProps } from 'hoc/InjectProps';
import useMediaQuery from 'hooks/useMediaQuery';
import { difference, maxBy, minBy } from 'lodash';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BaseTable, { AutoResizer, ColumnShape, RowKey } from 'react-base-table';
import 'react-base-table/styles.css';
import styled from 'styled-components';
import useSWR from 'swr';
import { WeekJumpTooltip } from '../WeekJumpTooltip';
import { SpaceGroup } from '../types';

const StyledSpaceTable = styled.div<{ size?: number }>`
    height: 100%;
    width: 100%;
    max-width: ${({ size }) => size + 'px'};

    .BaseTable__table-frozen-left {
        box-shadow: 4px 0 5px 0 #eeeeee;
    }

    .BaseTable__table-main .BaseTable__header-cell:last-child {
        padding: 0 7.5px;
    }

    .BaseTable__table-main .BaseTable__row-cell:last-child {
        padding-right: 0;
    }

    .BaseTable__row--hovered,
    .space-table-row:hover {
        background-color: inherit;
    }

    .BaseTable__row > .BaseTable__row-cell:first-of-type,
    .BaseTable__header-row > .BaseTable__header-cell:first-of-type {
        border-right: 1px solid rgb(200, 200, 200);
    }

    .space-table-cell {
        padding: 0;
        height: 50px;
        overflow: visible !important;
        position: relative;
    }

    .space-table-header-row {
        height: 30px;
    }
`;

const SpaceTableFooter = styled.div`
    height: 55px;
    width: 100%;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    border: 1px solid #eeeeee;

    > button.btn {
        border-radius: 0;
        height: 100%;
        width: 100%;

        svg {
            margin: 0 1rem;
        }
    }
`;

interface Props {
    spaceGroup: SpaceGroup;
    className?: string;
    isOwner?: boolean;
    autoRefresh?: number;
    clubSlug?: string;
}

export const SpaceTable = ({ spaceGroup, className, isOwner = false, autoRefresh, clubSlug }: Props) => {
    const tableRef = useRef<BaseTable>(undefined);
    const {
        query: { key }
    } = useRouter();
    const [weekOffset, setWeekOffset] = useState(0);
    const [expandedRowKeys, setExpandedRowKeys] = useState<RowKey[]>();
    const currentDate = moment().utc().startOf('isoWeek').add(weekOffset, 'week');
    const currentTimeIndex = moment().hours() * 4 + Math.floor(moment().minutes() / 15);
    const isMobile = useMediaQuery('xs');
    const renderQueryString = useMemo(() => {
        let year = currentDate.year();
        if (year !== currentDate.clone().add(6, 'days').year()) {
            year++;
        }
        return `${year}/${currentDate.week()}${key ? '/' + key : ''}`;
    }, [currentDate]);
    const { data, error } = useSWR(spaceGroup?.id ? `space/${spaceGroup?.id}/${renderQueryString}` : null, null, {
        refreshInterval: autoRefresh
    });
    const isLoading = !data && !error;
    const noRows = data?.data?.length <= 0;
    const cellWidth = 100;

    const rows: Array<{
        rowKey: string;
        isParent?: boolean;
        name: string;
        children: Array<MappedSpace>;
    }> = useMemo(
        () =>
            isLoading || noRows
                ? []
                : Array(7)
                      .fill(true)
                      .map((_, dayIndex) => {
                          const dayDate = currentDate.clone().day(dayIndex + 1);
                          return {
                              name: dayDate.format(isMobile ? 'D.M.YY' : 'dd, DD.MM.YY'),
                              rowKey: String(dayIndex + 1),
                              isParent: true,
                              children: data?.data?.map((space) => ({
                                  ...space,
                                  dayIndex,
                                  spaceGroup,
                                  date: dayDate,
                                  rowKey: space.id + '_' + dayIndex,
                                  blockings: space.blockings.filter((blocking) => {
                                      return (
                                          !blocking.exemptions?.some((exemption) => {
                                              let start = moment(exemption.start).utc(true);
                                              let end = moment(exemption.end).utc(true);

                                              if (start.isSame(end, 'day')) {
                                                  return dayDate.isSame(start, 'day');
                                              }

                                              return dayDate.isBetween(start, end, 'day', '[]');
                                          }) &&
                                          ((blocking.repeat_period?.toUpperCase() === 'DAILY' &&
                                              moment(blocking.repeat_until).isSameOrAfter(dayDate) &&
                                              moment(blocking.start).isSameOrBefore(dayDate)) ||
                                              blocking.day_index == dayIndex)
                                      );
                                  }),
                                  opening_hours: space.opening_hours[dayIndex]
                              }))
                          };
                      }),
        [currentDate, isLoading, noRows, isMobile]
    );

    const tableBorder = useMemo(() => {
        const tableBorders = Array(7)
            .fill(true)
            .map((_, dayIndex) => {
                const mappedData = data?.data?.map((space) => space.opening_hours[dayIndex]);
                return {
                    start: minBy(mappedData, (v: any) => (v.start === null ? 0 : v.start))?.start,
                    end: maxBy(mappedData, (v: any) => (v.end === null ? Infinity : v.end))?.end
                };
            });

        let start = minBy(tableBorders, (v: any) => (v.start === null ? 0 : v.start))?.start || 0;
        let end =
            maxBy(tableBorders, (v: any) => (v.end === null ? Infinity : v.end))?.end || refTable.times.length - 1;

        if (start % 4 !== 0)
            if (start - (start % 4) - 1 >= 0) start = start - (start % 4) - 1;
            else start = 0;

        if (end % 4 !== 0)
            if (end + (end % 4) < refTable.times.length) end = end - (end % 4);
            else end = refTable.times.length;

        return {
            start,
            end
        };
    }, [data]);

    const headerRenderer = useCallback(
        ({ cells, columns }: any) => {
            return cells.map((cell, index) => {
                if (index > 0) {
                    const cellIndex = Number(columns[index].key);

                    const children = (
                        <StyledTimeHeader>
                            <span>{columns[index].title}</span>
                            <small>bis {refTable.times?.[cellIndex + 4] || '00:00'}</small>
                        </StyledTimeHeader>
                    );

                    return React.cloneElement(cell, {
                        style: { ...cell.props.style, borderRight: '1px solid rgb(200, 200, 200)' },
                        children
                    });
                }

                return cell;
            });
        },
        [currentTimeIndex, isLoading]
    );

    const rowRenderer = useCallback(({ rowData, cells }: any) => {
        if (rowData.isParent) return cells[0];
        return cells;
    }, []);

    const rowClassName = useCallback(({ rowData }: any) => {
        let className = 'space-table-row';
        if (rowData.isParent) className += ' space-table-header-row';
        return className;
    }, []);

    const handleExpandRow = useCallback(
        (rowKey: string) => {
            if (tableRef.current) {
                const expandedRowKeys = tableRef.current.getExpandedRowKeys();
                if (expandedRowKeys.includes(rowKey))
                    tableRef.current.setExpandedRowKeys(expandedRowKeys.filter((k) => rowKey !== k));
                else tableRef.current.setExpandedRowKeys([...expandedRowKeys, rowKey]);
            }
        },
        [tableRef.current]
    );

    const handleWeekJump = (data) => {
        const date = data.weekToJump;
        const targetDate = moment(date).utc().startOf('isoWeek');
        const currentDate = moment().utc().startOf('isoWeek').add(weekOffset, 'week');
        const difference = targetDate.diff(currentDate, 'weeks');
        setWeekOffset(weekOffset + difference);
    };

    const columns: ColumnShape<{
        rowKey: string;
        isParent?: boolean;
        name: string;
        children: Array<MappedSpace>;
    }>[] = useMemo(() => {
        return [
            {
                key: 'space-header',
                title: 'Plätze',
                width: isMobile ? 100 : 140,
                dataKey: 'name',
                frozen: 'left',
                cellRenderer: ({ rowData, cellData }: { rowData: any; cellData: any }) => {
                    return (
                        <div
                            style={{
                                fontWeight: rowData.isParent ? 'bold' : 'inherit',
                                cursor: rowData.isParent ? 'pointer' : 'auto'
                            }}
                            onClick={() => handleExpandRow(rowData.rowKey)}
                        >
                            {cellData}
                        </div>
                    );
                }
            },
            ...(isLoading
                ? []
                : refTable.times
                      .map((time, index): ColumnShape => {
                          const { start, end } = tableBorder;
                          return index % 4 === 0 && index >= start && index <= end
                              ? {
                                    key: index,
                                    width: cellWidth,
                                    align: 'center',
                                    title: time,
                                    className: 'space-table-cell'
                                }
                              : null;
                      })
                      .filter((item) => item !== null))
        ];
    }, [tableBorder, isLoading]);

    // expand only current day and all future days
    useEffect(() => {
        const nextWeekDate = currentDate.clone().add(6, 'days');
        if (currentDate.isSameOrBefore(moment(), 'day') && nextWeekDate.isSameOrAfter(moment(), 'day')) {
            let weekdays = Array(moment().isoWeekday())
                .fill(true)
                .map((_, i) => String(i));
            setExpandedRowKeys(difference(['1', '2', '3', '4', '5', '6', '7'], weekdays));
        } else if (currentDate.isSameOrAfter(moment(), 'day')) {
            setExpandedRowKeys(['1', '2', '3', '4', '5', '6', '7']);
        } else {
            setExpandedRowKeys([]);
        }
    }, [weekOffset]);

    const size = ((tableBorder.end - tableBorder.start) / 2) * 50 + 140;

    return (
        <Flex justify="center" className="h-100">
            <StyledSpaceTable className={className} size={size}>
                <TableBar setWeekOffset={setWeekOffset} weekOffset={weekOffset} handleWeekJump={handleWeekJump} />
                <RefetchContextProvider spaceGroup={spaceGroup} selectedDate={currentDate}>
                    <AutoResizer>
                        {({ width, height }) => (
                            <BaseTable
                                ref={tableRef}
                                width={width}
                                height={height}
                                fixed
                                columns={columns}
                                disabled={isLoading || noRows}
                                data={rows}
                                rowRenderer={rowRenderer}
                                headerRenderer={headerRenderer}
                                rowKey="rowKey"
                                rowClassName={rowClassName}
                                components={{
                                    TableCell: InjectProps(DropCell, {
                                        spaceGroup,
                                        isOwner,
                                        selectedDate: currentDate,
                                        clubSlug
                                    }) as any,
                                    ExpandIcon
                                }}
                                footerHeight={55}
                                estimatedRowHeight={50}
                                expandColumnKey="space-header"
                                onExpandedRowsChange={(keys) => setExpandedRowKeys(keys)}
                                expandedRowKeys={expandedRowKeys}
                                emptyRenderer={<SpaceTableLoader empty={noRows} />}
                                footerRenderer={
                                    <TableBar
                                        setWeekOffset={setWeekOffset}
                                        weekOffset={weekOffset}
                                        handleWeekJump={handleWeekJump}
                                    />
                                }
                            />
                        )}
                    </AutoResizer>
                </RefetchContextProvider>
            </StyledSpaceTable>
        </Flex>
    );
};

const TableBar: React.FunctionComponent<
    React.PropsWithChildren<
        React.PropsWithChildren<{
            setWeekOffset: (wo: number) => any;
            weekOffset: number;
            handleWeekJump: (x: any) => any;
        }>
    >
> = ({ setWeekOffset, weekOffset, handleWeekJump }) => {
    const isMobile = useMediaQuery('(max-width: 650px)');
    return (
        <SpaceTableFooter>
            <Button onClick={() => setWeekOffset(weekOffset - 1)}>
                <FontAwesomeIcon icon={faArrowLeft} />
                {!isMobile && 'Woche zurück'}
            </Button>
            <Button disabled={weekOffset === 0} variant="light" onClick={() => setWeekOffset(0)}>
                {isMobile ? <FontAwesomeIcon icon={faHome} /> : 'Aktuelle Woche'}
            </Button>
            <WeekJumpTooltip handleWeekJump={handleWeekJump} />
            <Button onClick={() => setWeekOffset(weekOffset + 1)}>
                <FontAwesomeIcon icon={faArrowRight} />
                {!isMobile && 'Woche vor'}
            </Button>
        </SpaceTableFooter>
    );
};

const StyledTimeHeader = styled.div`
    line-height: 1.15 !important;

    span {
        display: block;
    }

    small {
        font-size: 60%;
        color: ${({ theme }) => theme.colors.secondary};
    }
`;

const StyledExpandIcon = styled.div`
    > svg {
        margin-right: 0.75rem;
        cursor: pointer;
    }
`;

const ExpandIcon = ({
    expandable,
    expanded,
    onExpand
}: {
    [key: string]: any;
    depth: number;
    expandable: boolean;
    expanded: boolean;
    onExpand: (expanded: boolean) => void;
}) => {
    return (
        <StyledExpandIcon>
            {expandable ? (
                <FontAwesomeIcon icon={!expanded ? faArrowRight : faArrowDown} onClick={() => onExpand(!expanded)} />
            ) : null}
        </StyledExpandIcon>
    );
};
